import { useState } from 'react';
import { Check, Plus, Search, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

interface AncillaryCode {
  cpt: string;
  description: string;
  selected: boolean;
}

const PRIMARY_PROCEDURE = {
  cpt: '27447',
  description: 'Total Knee Arthroplasty',
};

const ANCILLARY_PROCEDURES: AncillaryCode[] = [
  { cpt: '29875', description: 'Synovectomy', selected: false },
  { cpt: '29877', description: 'Chondroplasty', selected: false },
  { cpt: '29881', description: 'Meniscectomy', selected: false },
  { cpt: '27486', description: 'Revision TKA component', selected: false },
];

export function CptSelectorDemo() {
  const [ancillaries, setAncillaries] = useState<AncillaryCode[]>(ANCILLARY_PROCEDURES);
  const [isExpanded, setIsExpanded] = useState(true);

  const toggleCode = (cpt: string) => {
    setAncillaries((prev) =>
      prev.map((a) => (a.cpt === cpt ? { ...a, selected: !a.selected } : a))
    );
  };

  const selectedCount = ancillaries.filter((a) => a.selected).length;

  return (
    <div className="rounded-lg border border-border/60 bg-background/80 backdrop-blur overflow-hidden">
      {/* Header bar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border/40 bg-muted/30">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Procedures (CPT)</span>
      </div>

      {/* Primary procedure */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-border/30 hover:bg-accent/40 transition-colors"
      >
        <div className="flex items-center justify-center w-5 h-5 rounded bg-success/20 border border-success/30 shrink-0">
          <Check className="h-3 w-3 text-success" />
        </div>
        <div className="flex-1 text-left">
          <span className="text-xs font-mono text-success font-semibold">{PRIMARY_PROCEDURE.cpt}</span>
          <span className="text-xs text-foreground ml-2">{PRIMARY_PROCEDURE.description}</span>
        </div>
        <ChevronRight
          className={cn(
            'h-3.5 w-3.5 text-muted-foreground transition-transform duration-200',
            isExpanded && 'rotate-90'
          )}
        />
      </button>

      {/* Ancillary auto-expand */}
      {isExpanded && (
        <div className="bg-muted/10">
          <div className="px-3 py-1.5 border-b border-border/20">
            <span className="text-[10px] font-medium text-success/80 uppercase tracking-wider">
              Additional Procedures — Auto-Populated
            </span>
          </div>
          {ancillaries.map((code) => (
            <button
              key={code.cpt}
              onClick={() => toggleCode(code.cpt)}
              className="w-full flex items-center gap-2.5 px-3 py-2 hover:bg-accent/30 transition-colors border-b border-border/10 last:border-b-0"
            >
              <div
                className={cn(
                  'flex items-center justify-center w-5 h-5 rounded border shrink-0 transition-all duration-200',
                  code.selected
                    ? 'bg-success/20 border-success/40'
                    : 'bg-transparent border-border/50'
                )}
              >
                {code.selected ? (
                  <Check className="h-3 w-3 text-success" />
                ) : (
                  <Plus className="h-3 w-3 text-muted-foreground" />
                )}
              </div>
              <span className="text-xs font-mono text-muted-foreground">{code.cpt}</span>
              <span className="text-xs text-foreground/80">{code.description}</span>
            </button>
          ))}
        </div>
      )}

      {/* Footer status */}
      <div className="flex items-center justify-between px-3 py-2 border-t border-border/40 bg-muted/20">
        <span className="text-[10px] text-muted-foreground">
          {selectedCount + 1} of {ancillaries.length + 1} codes in bundle
        </span>
        <span className="text-[10px] font-medium text-success">
          Click to add →
        </span>
      </div>
    </div>
  );
}
